<script lang="ts" setup generic="S extends Step[]">
import {
  Fragment,
  onBeforeUnmount,
  type RendererElement,
  type RendererNode,
  shallowRef,
  type VNode,
  type VNodeArrayChildren,
} from 'vue';
import { computed, ref, useId, watch } from 'vue';

import KaMultiStepFormStep, { type KaMultiStepFormStepProps } from './KaMultiStepFormStep.vue';
import { provideMultiStepFormContext, scrollHorizontalSteps, type Step } from './model';

export type Props<S extends Step[]> = {
  steps: S;
  /**
   * Accessible name for the steps list. Should be a short noun phrase
   * describing the form's purpose, e.g. "Restore Steps", "Setup Steps".
   * Surfaced as `aria-label` on the `role="list"` container.
   */
  stepsListLabel?: string;
  /**
   * Optional supplementary description for the steps list, surfaced as
   * `aria-describedby`. Omit unless there's guidance the step names don't
   * already convey — `role="list"` announces the item count for free.
   */
  stepsListDescription?: string;
};
type InternalProps = { steps: Step[]; stepsListLabel?: string; stepsListDescription?: string };

const props = withDefaults(defineProps<Props<S>>(), {
  stepsListLabel: 'Form Steps',
}) as InternalProps;

const instanceId = useId();
const stepsListDescriptionId = computed(() =>
  props.stepsListDescription ? `${instanceId}-description` : undefined,
);
const stepRegionTestId = `${instanceId}-step-region`;

const emit = defineEmits<{
  (e: 'stepChange', newStep: S[number], prevStep: S[number]): void;
}>();

const slots = defineSlots<{
  default(): VNode[];
}>();

const stepListItemElements = ref<HTMLLIElement[]>();
const stepListElement = ref<HTMLOListElement>();
const stepRegionElement = ref<HTMLDivElement>();
const currentStepIdx = ref<number>(0);
const currentStepComponent = shallowRef<VNode>();

const currentStep = computed(() => props.steps[currentStepIdx.value]);
const nextStep = computed(() => props.steps[currentStepIdx.value + 1]);
const prevStep = computed(() => props.steps[currentStepIdx.value - 1]);

const stepRegionLabel = computed(
  () =>
    `Currently on: ${currentStep.value.name}. Step ${currentStepIdx.value + 1} of ${props.steps.length}.`,
);

const isFirstStep = computed(() => currentStepIdx.value === 0);
const isLastStep = computed(() => currentStepIdx.value + 1 === props.steps.length);

const currentComponentList = computed(() => {
  const componentList: VNode[] = [];

  type KaMultiStepFormStepVNode = VNode<RendererNode, RendererElement, KaMultiStepFormStepProps>;

  const isMultiStepFormStepVNode = (node: VNode): node is KaMultiStepFormStepVNode =>
    node.type === KaMultiStepFormStep;

  // traverse iterates over Vue's virtual subtree to find all KaMultiStepFormStep components
  const traverse = (nodes: VNodeArrayChildren) => {
    for (const node of nodes) {
      // If node is an array - go deeper
      if (Array.isArray(node)) {
        traverse(node);
      }
      // If node is primitive it can't be a step
      else if (!node || typeof node !== 'object' || !('type' in node)) {
        continue;
      } else if (node.type === Fragment && Array.isArray(node.children)) {
        traverse(node.children);
      } else if (isMultiStepFormStepVNode(node)) {
        componentList.push(node);
      }
    }
  };

  traverse(slots.default());

  return componentList;
});

const handleNext = (isLast: boolean) => {
  if (isLast) {
    currentStepIdx.value = props.steps.length - 1;
    return;
  }
  if (props.steps.length > currentStepIdx.value + 1) {
    currentStepIdx.value++;
  }
};
const handleBack = (isFirst: boolean) => {
  if (isFirst) {
    currentStepIdx.value = 0;
    return;
  }
  if (currentStepIdx.value > 0) {
    currentStepIdx.value--;
  }
};

const listResizeObserver = new ResizeObserver(() =>
  scrollHorizontalSteps(currentStepIdx.value, stepListElement.value, stepListItemElements.value),
);

provideMultiStepFormContext({
  isFirstStep,
  isLastStep,
  currentStep,
  nextStep,
  prevStep,
  onBack: () => handleBack(false),
  onNext: () => handleNext(false),
  onFirst: () => handleBack(true),
  onLast: () => handleNext(true),
});

watch(
  [currentStepIdx, currentStep, currentComponentList],
  ([stepIdx, step, _currentComponentList], [prevStepIdx]) => {
    const placeIdx = _currentComponentList.findIndex(
      ({ props, type }) => type === KaMultiStepFormStep && props?.id === step.id,
    );

    currentStepComponent.value = placeIdx < 0 ? undefined : _currentComponentList[placeIdx];

    if (prevStepIdx !== undefined) {
      emit('stepChange', props.steps[stepIdx], props.steps[prevStepIdx]);
    }
  },
  { immediate: true, deep: false },
);

watch([currentStepIdx, stepListElement, stepListItemElements], ([index, listEl, listItemEls]) =>
  scrollHorizontalSteps(index, listEl, listItemEls),
);

// Next/Back remounts the step subtree, dropping focus to <body> (K10-36278).
// Not `immediate`, so this only runs on real transitions, never on mount.
watch(
  currentStepIdx,
  () => {
    stepRegionElement.value?.focus();
  },
  { flush: 'post' },
);

watch(
  stepListElement,
  (listEl, prevListEl) => {
    if (prevListEl !== undefined) {
      listResizeObserver.unobserve(listEl as any); // TODO https://kasten.atlassian.net/browse/K10-36144
    }
    if (listEl !== undefined) {
      listResizeObserver.observe(listEl);
    }
  },
  { immediate: true, flush: 'sync' },
);
onBeforeUnmount(() => {
  listResizeObserver.disconnect();
});
</script>
<template>
  <div class="flex gap-12 max-lg:flex-col lg:gap-4">
    <div class="flex-shrink-0 max-lg:items-center lg:w-[25%] lg:max-w-[260px] lg:justify-start">
      <div class="flex flex-col lg:sticky lg:top-3">
        <span v-if="props.stepsListDescription" :id="stepsListDescriptionId" class="sr-only">{{
          props.stepsListDescription
        }}</span>
        <ol
          ref="stepListElement"
          class="flex list-none max-sm:flex-col max-sm:gap-3 sm:max-lg:-mb-4 sm:max-lg:w-full sm:max-lg:overflow-x-auto sm:max-lg:pb-4 lg:flex-col lg:gap-3"
          :aria-label="props.stepsListLabel"
          :aria-describedby="stepsListDescriptionId"
        >
          <li
            v-for="(step, index) in props.steps"
            :key="step.name"
            ref="stepListItemElements"
            :aria-current="currentStepIdx === index ? 'step' : undefined"
            :class="[
              'relative flex flex-shrink-0 basis-0 items-center gap-1 max-sm:gap-3 sm:max-lg:flex-grow sm:max-lg:flex-col sm:max-lg:justify-center lg:gap-3 sm:max-lg:[&:not(:first-child)]:pl-2 sm:max-lg:[&:not(:last-child)]:pr-2',
              [
                '[&:not(:first-child):before]:absolute [&:not(:first-child):before]:right-1/2 [&:not(:first-child):before]:top-[4px] [&:not(:first-child):before]:w-full [&:not(:first-child):before]:border-b-2',
                `[&:not(:first-child):before]:border-gray-300 [&:not(:first-child):before]:content-[''] dark:[&:not(:first-child):before]:border-navy-600 [&:not(:first-child):before]:max-sm:hidden [&:not(:first-child):before]:lg:hidden`,
                currentStepIdx >= index &&
                  '[&:not(:first-child):before]:border-blue-600 dark:[&:not(:first-child):before]:border-blue-400',
              ],
            ]"
          >
            <div class="z-10 bg-white dark:bg-navy-950 sm:max-lg:px-3.5">
              <div
                class="h-2.5 w-2.5 rounded-full max-sm:mt-[1px] lg:mt-[1px]"
                :class="{
                  'border border-gray-700 dark:border-gray-300': currentStepIdx < index,
                  'border-2 border-blue-600 bg-blue-600 dark:border-blue-400 dark:bg-blue-400':
                    currentStepIdx > index,
                  'border-2 border-blue-600 dark:border-blue-400': currentStepIdx === index,
                }"
              />
            </div>
            <span
              class="whitespace-nowrap text-sm"
              :class="{
                'text-gray-900 dark:text-gray-400': currentStepIdx !== index,
                'font-bold text-blue-600 dark:text-blue-400': currentStepIdx === index,
              }"
              >{{ step.name }}</span
            >
          </li>
        </ol>
      </div>
    </div>
    <div
      ref="stepRegionElement"
      tabindex="-1"
      role="region"
      :aria-label="stepRegionLabel"
      :data-testid="stepRegionTestId"
      class="flex min-w-0 flex-grow flex-col focus-visible:outline-2 focus-visible:outline-blue-400"
    >
      <component
        :is="currentStepComponent"
        v-if="currentStepComponent"
        :key="
          // <component> by default doesn't unmount the component if the new one is
          // of the same component path (e.g. component type + key if exists).
          // So this is to force entire remount.
          currentStep.id
        "
      />
    </div>
  </div>
</template>
